// Kiểu dữ liệu khớp với JSON của backend (internal/api).
//
// KHÔNG suy diễn thêm trường nào ở đây. Nếu thêm trường mà backend không
// gửi, TypeScript vẫn cho qua và giá trị là `undefined` — lỗi hiện ra lúc
// chạy, ở màn nào tùy tình cờ. Để thiếu thành thiếu thì `tsc` chặn ngay.

export interface User {
  user_id: string
  email: string
  /** false = tài khoản chỉ đăng nhập bằng Google/Facebook, không có mật khẩu để
   *  đổi. Server trả kèm nhưng kiểu này trước đây bỏ trống. */
  has_password: boolean
  /** Có quyền kho đầy đủ (không bị giới hạn về tập mẫu). */
  full_library: boolean
}

/** Gói bán. Giá và số tháng do server quyết định — xem `plans` ở
 *  internal/api/orders.go. ⚠️ Đánh dấu GIÁ TẠM, chưa chốt với team. */
export interface Plan {
  months: number
  amount_vnd: number
}

export interface Order {
  id: string
  /** `sepay` | `play` | `fake`. */
  provider: string
  plan_months: number
  amount_vnd: number
  status: 'pending' | 'paid' | 'failed' | 'expired'
  /** Ảnh QR (SePay). Rỗng với provider `play`: UI mua là của Google. */
  pay_url: string
  created_at: string
}

export interface Option {
  /** Key gốc trong DB. Gửi lên server khi trả lời — KHÔNG dùng để hiển thị. */
  key: string
  /** Chữ hiển thị A, B, C, D theo thứ tự đã trộn. */
  label: string
  text: string
}

export interface Question {
  /** Khoá gửi lên khi trả lời. KHÁC `id` bên dưới — xem ghi chú ở backend. */
  session_question_id: string
  /** question_version_id — định danh nội dung. */
  id: string
  code: string
  situation: string
  question: string
  options: Option[]
  /** Slug đề mục, ví dụ `he-ho-hap`. Chỉ dùng khi cần tra cứu. */
  topic: string
  /** Tên đề mục để hiện ra, ví dụ `Hệ hô hấp`. Rỗng nếu taxonomy chưa nạp. */
  topic_name?: string
}

export interface ResultItem {
  code: string
  selected: string
  correct_key: string
  correct: boolean
  /** false = học viên bỏ trống câu này, khác với trả lời sai. */
  answered: boolean
  explanation?: string
  takeaway?: string
}

export interface Result {
  score_correct: number
  score_total: number
  percent: number
  items: ResultItem[]
  submitted_late: boolean
  buffer_sec: number
}

export interface Session {
  id: string
  mode: string
  question_count: number
  /** null = không giới hạn (mode=practice). */
  time_limit_sec: number | null
  started_at: string
  /** Mốc thời gian server — client dùng để hiệu chỉnh đồng hồ đếm ngược. */
  server_now: string
  /** -1 = không giới hạn. */
  seconds_left: number
  submitted_at?: string
  questions: Question[]
  result?: Result
}

export interface Topic {
  slug: string
  name: string
  sort_order: number
  question_count: number
  sample_count: number
  /** Số câu user này thực làm được: có quyền thì cả kho, không thì tập mẫu. */
  available: number
  attempts: number
  correct: number
  /** -1 = chưa làm câu nào. Phân biệt với 0% — xem ghi chú ở backend. */
  accuracy: number
  empty: boolean
}

export interface DayStat {
  date: string
  answered: number
  correct: number
}

/** Một câu đã làm sai và chưa làm lại đúng. */
export interface WrongQuestion {
  /** Khoá nội dung câu — dùng để tạo phiên ôn lại đúng câu này. */
  question_version_id: string
  code: string
  topic: string
  topic_name?: string
  /** 120 ký tự đầu, đủ để nhận ra câu mà không tải cả nội dung. */
  preview: string
  times_wrong: number
  last_wrong_at: string
}

/** Số câu sai của một đề mục — dùng vẽ biểu đồ cột ngang. */
export interface WrongTopicCount {
  topic: string
  topic_name: string
  count: number
}

export interface Progress {
  has_full: boolean
  sessions_done: number
  sessions_open: number
  answered: number
  answered_correct: number
  accuracy: number
  streak_days: number
  due_for_review: number
  by_day: DayStat[]
}

export interface CoreIssue {
  slug: string
  name: string
  question_count: number
}

/** Lỗi có mã từ backend — dùng `code` để quyết định phản ứng, không đoán
 *  theo chuỗi tiếng Việt trong `detail` (nó có thể đổi bất cứ lúc nào). */
export class ApiError extends Error {
  code: string
  status: number
  detail: string

  constructor(status: number, code: string, detail: string) {
    super(`${code}: ${detail}`)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.detail = detail
  }
}
